import { TotalDataCreateContent } from 'interfaces'
import { useMemo } from 'react'

interface DeliveryEnhancementsResult {
	hasPushNotification: boolean
	hasGoodlink: boolean
	hasLocation: boolean
	hasSchedule: boolean
	hasAudience: boolean
}

export const useDeliveryEnhancements = (
	deliveryEnhancements: TotalDataCreateContent['delivery_enhancements']
): DeliveryEnhancementsResult => {
	const hasPushNotification = useMemo(() => {
		if (!deliveryEnhancements.push_notification) return false

		const {
			title,
			message,
			is_message: isMessage,
			is_title: isTitle
		} = deliveryEnhancements.push_notification

		if (!isMessage && !isTitle) return true
		if (isMessage && typeof message === 'string' && message.trim() !== '')
			return true
		if (isTitle && typeof title === 'string' && title.trim() !== '') return true

		return false
	}, [deliveryEnhancements.push_notification])

	const hasGoodlink = useMemo(
		() =>
			Boolean(
				deliveryEnhancements.good_link?.link_value ||
					deliveryEnhancements.good_link?.sublink_type
			),
		[deliveryEnhancements.good_link]
	)

	const hasLocation = useMemo(() => {
		if (!deliveryEnhancements.location?.length) return false

		const contentLocation = deliveryEnhancements.location[0]
		return Boolean(
			contentLocation?.latitude ||
				contentLocation?.longitude ||
				contentLocation?.location_id
		)
	}, [deliveryEnhancements.location])

	const hasSchedule = useMemo(() => {
		if (!deliveryEnhancements.schedule) return false

		const {
			timing,
			event,
			custom_recurrence: customRecurrence
		} = deliveryEnhancements.schedule

		return Boolean(
			timing?.release_date ||
				timing?.expiration_date ||
				event?.event_start_date ||
				event?.event_end_date ||
				customRecurrence?.repeat_start_date
		)
	}, [deliveryEnhancements.schedule])

	const hasAudience = useMemo(
		() =>
			Object.values(deliveryEnhancements.audience).some(
				array => Array.isArray(array) && array.length > 0
			),
		[deliveryEnhancements.audience]
	)

	return {
		hasPushNotification,
		hasGoodlink,
		hasLocation,
		hasSchedule,
		hasAudience
	}
}
